import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Tv as TvIcon,
  ViewModule as SlotsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { locationAPI } from '../services/api';

const STATE_ABBREVIATIONS = {
  'karnataka': 'KA',
  'maharashtra': 'MH',
  'tamil nadu': 'TN',
  'telangana': 'TG',
  'andhra pradesh': 'AP',
  'uttar pradesh': 'UP',
  'west bengal': 'WB',
  'punjab': 'PB',
  'haryana': 'HR',
  'rajasthan': 'RJ',
  'delhi': 'DL',
};

const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStateCode = (state) => {
  if (!state) return '';
  const lowerState = state.toLowerCase();
  return STATE_ABBREVIATIONS[lowerState] || state.toUpperCase().slice(0, 2);
};

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    locationName: '',
    locationId: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    operatingHours: {
      startTime: '10:00',
      endTime: '22:00',
    },
  });

  useEffect(() => {
    fetchLocations();
    fetchStats();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await locationAPI.getAllLocations();
      // Handle different response structures
      const locationsData = response.data?.data?.locations || response.data?.locations || response.data || [];
      setLocations(Array.isArray(locationsData) ? locationsData : []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
      setLocations([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await locationAPI.getLocationStats();
      setStats(response.data?.data || response.data || null);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(null);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditMode(true);
      setSelectedLocation(location);
      setFormData({
        locationName: location.locationName || location.name || '',
        locationId: location.locationId || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        pincode: location.pincode || '',
        operatingHours: location.operatingHours || { startTime: '10:00', endTime: '22:00' },
      });
    } else {
      setEditMode(false);
      setSelectedLocation(null);
      setFormData({
        locationName: '',
        locationId: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        operatingHours: {
          startTime: '10:00',
          endTime: '22:00',
        },
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedLocation(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('operatingHours.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        operatingHours: {
          ...formData.operatingHours,
          [field]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await locationAPI.updateLocation(selectedLocation._id, formData);
        toast.success('Location updated successfully');
      } else {
        await locationAPI.createLocation(formData);
        toast.success('Location created successfully');
      }
      handleCloseDialog();
      fetchLocations();
      fetchStats();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.message || 'Failed to save location');
    }
  };

  const handleDelete = async (id, forceDelete = false) => {
    const location = locations.find(loc => loc._id === id);
    const locationName = location?.locationName || location?.name || 'this location';
    
    const confirmMessage = forceDelete
      ? `Are you sure you want to FORCE DELETE ${locationName}? This will also delete all associated screens. This action cannot be undone!`
      : `Are you sure you want to delete ${locationName}?`;

    if (window.confirm(confirmMessage)) {
      try {
        await locationAPI.deleteLocation(id, forceDelete);
        toast.success('Location deleted successfully');
        fetchLocations();
        fetchStats();
      } catch (error) {
        console.error('Error deleting location:', error);
        const errorMessage = error.response?.data?.message || 'Failed to delete location';
        const screens = error.response?.data?.screens;

        // If there are screens blocking deletion, show detailed error with option to force delete
        if (screens && screens.length > 0) {
          const screenList = screens.map(s => `${s.screenId} (${s.name})`).join(', ');
          const detailedMessage = `${errorMessage}\n\nScreens: ${screenList}`;
          
          toast.error(detailedMessage, { autoClose: 8000 });

          // Ask if user wants to force delete
          setTimeout(() => {
            if (window.confirm(`${errorMessage}\n\nDo you want to FORCE DELETE this location and all its screens?`)) {
              handleDelete(id, true);
            }
          }, 500);
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Location Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Location
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && stats.overview && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <LocationIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.overview.totalLocations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Locations
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TvIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.overview.totalScreens}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Screens
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <SlotsIcon color="warning" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.overview.totalSlots}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Slots
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Locations Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'background.default',
                  '& .MuiTableCell-head': {
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    padding: '14px 16px',
                  },
                }}
              >
                <TableCell sx={{ width: '16%' }}>Location Name</TableCell>
                <TableCell sx={{ width: '20%' }}>Address</TableCell>
                <TableCell sx={{ width: '12%' }}>City / State</TableCell>
                <TableCell sx={{ width: '14%' }}>Operating Hours</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>Screens</TableCell>
                <TableCell align="center" sx={{ width: '12%' }}>Slots</TableCell>
                <TableCell align="center" sx={{ width: '12%' }}>Occupancy</TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <LocationIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        No locations yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get started by creating your first location
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                      >
                        Create Location
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location) => (
                  <TableRow 
                    key={location._id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      transition: 'all 0.2s ease',
                      '& .MuiTableCell-root': {
                        padding: '12px 16px',
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '16%' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography fontWeight="600" sx={{ fontSize: '0.9rem', color: '#1e293b' }}>
                          {toTitleCase(location.locationName || location.name)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '20%', fontSize: '0.9rem', color: '#64748b' }}>
                      {location.address}
                    </TableCell>
                    <TableCell sx={{ width: '12%', fontSize: '0.9rem', color: '#1e293b', fontWeight: 600 }}>
                      {toTitleCase(location.city || '-')}, {getStateCode(location.state || '-')}
                    </TableCell>
                    <TableCell>
                      {location.operatingHours?.startTime || location.operatingHours?.start} - {location.operatingHours?.endTime || location.operatingHours?.end}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${location.totalScreens || 0} screens assigned to this location`} arrow>
                        <Box sx={{ display: 'inline-block' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#7C3AED', fontSize: '1rem' }}>
                            {location.totalScreens || '—'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${location.filledSlots || 0} of ${location.totalSlots || 0} total slots in use`} arrow>
                        <Box sx={{ display: 'inline-block' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                            {location.totalSlots === 0 || !location.totalSlots ? '—' : `${location.filledSlots || 0}/${location.totalSlots || 0}`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip 
                        title={`${location.filledSlots || 0} of ${location.totalSlots || 0} slots used`}
                        arrow
                      >
                        <Box sx={{ width: '100%', maxWidth: 120, mx: 'auto' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {location.filledSlots || 0}/{location.totalSlots || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: location.occupancyRate >= 80 ? '#dc2626' : location.occupancyRate >= 50 ? '#f59e0b' : '#22c55e', fontSize: '0.8rem' }}>
                              {location.occupancyRate || 0}%
                            </Typography>
                          </Box>
                          {location.occupancyRate === 0 ? (
                            <Box
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#d1d5db',
                                border: '1px solid #d1d5db',
                              }}
                            />
                          ) : (
                            <LinearProgress 
                              variant="determinate" 
                              value={location.occupancyRate || 0}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: location.occupancyRate >= 80 ? '#dc2626' : location.occupancyRate >= 50 ? '#f59e0b' : '#22c55e',
                                  borderRadius: 3,
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                        <Tooltip title="Edit Location" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(location)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Location" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(location._id)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location Name"
                  name="locationName"
                  value={formData.locationName}
                  onChange={handleInputChange}
                  required
                  helperText="Display name for the location"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location ID"
                  name="locationId"
                  value={formData.locationId}
                  onChange={handleInputChange}
                  required
                  disabled={editMode}
                  helperText={editMode ? "Cannot change location ID" : "Unique identifier (e.g., LOC001)"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  type="number"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  required
                  inputProps={{ pattern: '[0-9]{6}', placeholder: '6 digit pincode' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Opening Time"
                  name="operatingHours.startTime"
                  type="time"
                  value={formData.operatingHours.startTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Closing Time"
                  name="operatingHours.endTime"
                  type="time"
                  value={formData.operatingHours.endTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Locations;